/** @jsx jsx */
import * as React from 'react';
import { jsx } from '@compiled/react';
import { isDateTimeSupportEnabled } from '@helpcenter/feature-flags';
import type { FieldFormProps } from '@helpcenter/field-types';
import { parseISO, isValid as isValidDate, formatISO } from 'date-fns';
import { isEmpty } from 'lodash';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import { FieldBase } from 'view/common/fields/field-base';
import messages from 'view/common/messages';
import { css } from '@atlaskit/css';
import { DateTimePicker } from '@atlaskit/datetime-picker';
import dateNow from '@atlassian/help-center-common-util/date-now';
import { format, getWeekStartDay } from '@atlassian/help-center-common-util/date-picker-utils';
import { splitDateTimeFormat } from '@atlassian/help-center-common-util/unix-time-parser';
import { withPortalLocale } from '../../with-portal-locale';

/**
 * Processes a date-time value by validating and converting to ISO format
 * @param value - The input date-time string to process
 * @returns Processed date-time string in ISO format or undefined if invalid
 */
export const processDateTimeValue = (value?: string): string | undefined => {
	if (!value || !isValidDate(new Date(value))) {
		return undefined;
	}
	return formatISO(new Date(value));
};

export interface DateTimePickerProps extends FieldFormProps<string>, WrappedComponentProps {
	dateTimeFormat?: string;
	defaultValue?: string;
	firstDay?: number;
	useISO8601WeekNumbers?: boolean;
	locale: string;
	value?: string;
}

// Extract props from @atlaskit/datetime-picker
type AKDateTimePickerProps = ExtractProps<typeof DateTimePicker>;
type AKDatePickerProps = AKDateTimePickerProps['datePickerProps'];
type AKTimePickerProps = AKDateTimePickerProps['timePickerProps'];

const AVAILABLE_TIMES = [
	'00:00',
	'00:30',
	'01:00',
	'01:30',
	'02:00',
	'02:30',
	'03:00',
	'03:30',
	'04:00',
	'04:30',
	'05:00',
	'05:30',
	'06:00',
	'06:30',
	'07:00',
	'07:30',
	'08:00',
	'08:30',
	'09:00',
	'09:30',
	'10:00',
	'10:30',
	'11:00',
	'11:30',
	'12:00',
	'12:30',
	'13:00',
	'13:30',
	'14:00',
	'14:30',
	'15:00',
	'15:30',
	'16:00',
	'16:30',
	'17:00',
	'17:30',
	'18:00',
	'18:30',
	'19:00',
	'19:30',
	'20:00',
	'20:30',
	'21:00',
	'21:30',
	'22:00',
	'22:30',
	'23:00',
	'23:30',
];

// TODO: Add proper locale support so that this workaround to fix date parsing isn't needed
const parseInputValue = (date: string): Date => {
	if (isValidDate(new Date(date))) {
		return parseISO(new Date(date).toISOString());
	}
	return parseISO(new Date().toISOString());
};
export class CustomDateTimePicker extends React.PureComponent<DateTimePickerProps> {
	onChange = (isoTime: string) => {
		const { onChange } = this.props;

		if (isDateTimeSupportEnabled()) {
			onChange &&
				onChange(
					this.props.id,
					isoTime ? format(parseISO(isoTime), "yyyy-MM-dd'T'HH:mm") : isoTime,
					false,
					{
						rawObject: {},
						rawValue: isoTime,
					},
				);
		} else {
			onChange &&
				onChange(
					this.props.id,
					isoTime ? format(parseISO(isoTime), "yyyy-MM-dd'T'HH:mm") : isoTime,
					false,
				);
		}
	};

	// We need custom parse date and parse time functions here so the date and time are displayed in the right locale
	parseDateLabel = (value: string, dateFormat: string): string => {
		return value && dateFormat ? format(parseISO(value), dateFormat) : value;
	};

	parseTimeLabel = (value: string, timeFormat: string): string => {
		// date-fns format can't parse a time without a date prefix, in this case we will manually append
		// a date to the time so that it will format it correctly
		return value && timeFormat ? format(parseISO(`1970-01-01 ${value}`), timeFormat) : value;
	};

	render() {
		const {
			id,
			label,
			locale,
			required,
			defaultValue,
			description,
			errorMessage,
			disabled,
			firstDay,
			useISO8601WeekNumbers,
			intl,
			value,
			dateTimeFormat = 'yyyy-MM-dd hh:mm a',
		} = this.props;
		const isInvalid = !isEmpty(errorMessage);
		const { dateFormat, timeFormat } = splitDateTimeFormat(dateTimeFormat);
		// Need to refer to id like this.
		// See: https://atlaskit.atlassian.com/packages/core/datetime-picker
		const inputId = `react-select-${id}-input`;
		const defaultDateTimeValue = processDateTimeValue(defaultValue);

		// this logic is derived from the default value logic above
		const processedValue =
			value && isDateTimeSupportEnabled() ? processDateTimeValue(value) : undefined;

		const datePickerProps: Partial<AKDatePickerProps> = {
			dateFormat,
			parseInputValue,
			formatDisplayLabel: this.parseDateLabel,
			placeholder: intl.formatMessage(messages.forExample, {
				text: format(dateNow(), dateFormat),
			}),
			weekStartDay: getWeekStartDay(firstDay, useISO8601WeekNumbers),
		};
		const timePickerProps: Partial<AKTimePickerProps> = {
			timeFormat,
			timeIsEditable: true,
			formatDisplayLabel: this.parseTimeLabel,
			times: AVAILABLE_TIMES,
			placeholder: intl.formatMessage(messages.forExample, {
				text: format(dateNow(), timeFormat),
			}),
		};

		return (
			<FieldBase
				htmlFor={inputId}
				label={label}
				required={required}
				errorMessage={errorMessage}
				description={description}
			>
				<div css={dataTimePickerContainer}>
					<DateTimePicker
						id={inputId}
						name={id}
						defaultValue={defaultDateTimeValue}
						onChange={this.onChange}
						isInvalid={isInvalid}
						// Assertions necessary due to broken types in @atlaskit/datetime-picker
						// requiring extra props to be passed even though they have defaults
						datePickerProps={datePickerProps as AKDatePickerProps}
						timePickerProps={timePickerProps as AKTimePickerProps}
						isDisabled={!!disabled}
						locale={locale}
						aria-describedby={`${inputId}-field-description`}
						value={isDateTimeSupportEnabled() ? processedValue : undefined}
					/>
				</div>
			</FieldBase>
		);
	}
}

export default withPortalLocale(injectIntl(CustomDateTimePicker));

const dataTimePickerContainer = css({
	width: '280px', // grid.multiple(35).px
});
